import { useCallback, useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FloppyDisk, X, EyeSlash, ImageSquare, UploadSimple, Star, Sparkle } from "@phosphor-icons/react";

const EMPTY_FORM = {
  name: "", short_description: "", category: "",
  hidden_prompt: "", sort_order: 0, is_active: true,
  // CDS-upgrade UI fields (safe defaults)
  tags: "",                         // comma-separated in UI, split on save
  is_featured: false, is_quickstart: false, is_fresh_drop: false,
  featured_until: "",
  quickstart_sort_order: "", fresh_drop_sort_order: "", featured_sort_order: "",
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm outline-none focus:border-amber-400/60";

// Admin-only manager: CRUD for creative_director_prompts + global default design direction.
export default function CreativeDirectorManager({ onChanged }) {
  const [list, setList] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [defaultDir, setDefaultDir] = useState("");
  const [dirLoaded, setDirLoaded] = useState(false);
  const [savingDir, setSavingDir] = useState(false);
  // ---- CDS-upgrade: thumbnail state (per-form, resets on open/close) ----
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [currentThumb, setCurrentThumb] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/creative-directors"),
        api.get("/admin/settings/design-direction"),
      ]);
      setList(r1.data || []);
      setDefaultDir(r2.data?.prompt || "");
      setDirLoaded(true);
    } catch (_) {
      toast.error("Couldn't load Creative Director settings.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const notify = () => { load(); onChanged?.(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setThumbPreview(null); setThumbFile(null); setFormOpen(true); };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({
      name: d.name || "", short_description: d.short_description || "", category: d.category || "",
      hidden_prompt: d.hidden_prompt || "", sort_order: d.sort_order ?? 0, is_active: !!d.is_active,
      // CDS-upgrade UI fields
      tags: Array.isArray(d.tags) ? d.tags.join(", ") : "",
      is_featured: !!d.is_featured, is_quickstart: !!d.is_quickstart, is_fresh_drop: !!d.is_fresh_drop,
      featured_until: d.featured_until || "",
      quickstart_sort_order: d.quickstart_sort_order ?? "",
      fresh_drop_sort_order: d.fresh_drop_sort_order ?? "",
      featured_sort_order: d.featured_sort_order ?? "",
    });
    setCurrentThumb(d.thumbnail || null);
    setThumbPreview(null); setThumbFile(null);
    setFormOpen(true);
  };

  // ---------- Thumbnail helpers ----------
  // For a CREATE flow we defer the upload until the record exists (we need
  // the director_id). For an EDIT flow we upload immediately and update the
  // current thumbnail badge in place.
  const onPickThumb = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Thumbnail must be under 8 MB."); return; }
    setThumbFile(file);
    const reader = new FileReader();
    reader.onload = () => setThumbPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearPickedThumb = () => { setThumbFile(null); setThumbPreview(null); };

  const uploadThumbFor = async (directorId) => {
    if (!thumbFile) return null;
    const fd = new FormData();
    fd.append("file", thumbFile);
    const r = await api.post(`/admin/creative-directors/${directorId}/thumbnail`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data?.thumbnail || null;
  };

  const removeThumbFor = async (directorId) => {
    await api.delete(`/admin/creative-directors/${directorId}/thumbnail`);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Director name is required."); return; }
    setSaving(true);
    try {
      // Build payload — split tags CSV into array, coerce numerics, drop
      // empty section-sort-order fields so the server keeps them null.
      const payload = {
        name: form.name, short_description: form.short_description, category: form.category,
        hidden_prompt: form.hidden_prompt,
        sort_order: parseInt(form.sort_order, 10) || 0,
        is_active: form.is_active,
        tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
        is_featured: form.is_featured, is_quickstart: form.is_quickstart, is_fresh_drop: form.is_fresh_drop,
      };
      if (form.featured_until) payload.featured_until = form.featured_until;
      const secKeys = ["quickstart_sort_order", "fresh_drop_sort_order", "featured_sort_order"];
      secKeys.forEach((k) => {
        const v = form[k];
        if (v !== "" && v !== null && v !== undefined) payload[k] = parseInt(v, 10) || 0;
      });

      let directorId = editing;
      if (editing) {
        await api.put(`/admin/creative-directors/${editing}`, payload);
      } else {
        const created = await api.post("/admin/creative-directors", payload);
        directorId = created.data?.id;
      }
      // Thumbnail upload happens AFTER the record write so a failed upload
      // never leaves the record pointing at a missing storage object.
      if (thumbFile && directorId) {
        try { await uploadThumbFor(directorId); }
        catch (upErr) {
          const d = upErr?.response?.data?.detail;
          toast.error(typeof d === "string" ? d : "Thumbnail upload failed. The record was saved but the image was not.");
        }
      }
      toast.success(editing ? "Director updated — changes are live." : "Director created — live in AI Pro Studio now.");
      setFormOpen(false);
      setThumbFile(null); setThumbPreview(null); setCurrentThumb(null);
      notify();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally { setSaving(false); }
  };

  // Called from the edit form only. Immediately clears the thumbnail on the
  // server; the UI reflects it on the next refresh.
  const removeExistingThumbnail = async () => {
    if (!editing) return;
    if (!window.confirm("Remove this director's thumbnail? The card will show the placeholder.")) return;
    try {
      await removeThumbFor(editing);
      setCurrentThumb(null);
      toast.success("Thumbnail removed — placeholder is now shown.");
      notify();
    } catch (_) { toast.error("Could not remove the thumbnail."); }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/admin/creative-directors/${d.id}`, { is_active: !d.is_active });
      toast.success(d.is_active ? `"${d.name}" hidden from users.` : `"${d.name}" is now live.`);
      notify();
    } catch (_) { toast.error("Could not update."); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? Users will no longer see this option.`)) return;
    try {
      await api.delete(`/admin/creative-directors/${d.id}`);
      toast.success("Deleted.");
      notify();
    } catch (_) { toast.error("Delete failed."); }
  };

  const saveDefaultDir = async () => {
    setSavingDir(true);
    try {
      await api.put("/admin/settings/design-direction", { prompt: defaultDir });
      toast.success("Default design direction saved — silently applied to every future generation.");
    } catch (_) { toast.error("Save failed."); }
    finally { setSavingDir(false); }
  };

  return (
    <div data-testid="director-manager" className="flex flex-col gap-6">
      {/* Prompt list */}
      <div className="rounded-2xl border border-white/10 bg-[#111] p-5">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <p className="font-display text-base font-bold">Creative Director Prompts</p>
          <span className="text-[11px] text-zinc-500">Active presets appear instantly in AI Pro Studio — no redeploy.</span>
          <button
            data-testid="dm-create-btn"
            onClick={openCreate}
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-bold transition-colors"
          >
            <Plus size={14} weight="bold" /> Create New Prompt
          </button>
        </div>

        {formOpen && (
          <div data-testid="dm-form" className="rounded-xl border border-amber-400/30 bg-black/30 p-4 mb-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-amber-200">{editing ? "Edit Director" : "New Director"}</p>
              <button data-testid="dm-form-close" onClick={() => setFormOpen(false)} className="text-zinc-500 hover:text-white"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Director Name">
                <input data-testid="dm-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Luxury Magazine" className={inputCls} />
              </Field>
              <Field label="Category / Tag">
                <input data-testid="dm-category-input" value={form.category} onChange={set("category")} placeholder='e.g. Luxury — use "surprise-me" for a random pick' className={inputCls} />
              </Field>
            </div>
            <Field label="Short Display Description (users see this)">
              <input data-testid="dm-desc-input" value={form.short_description} onChange={set("short_description")} placeholder="One line shown on the card in AI Pro Studio" className={inputCls} />
            </Field>
            <Field label="Hidden GPT Image 2 Prompt Instructions (never shown to users)">
              <textarea
                data-testid="dm-hidden-input"
                value={form.hidden_prompt}
                onChange={set("hidden_prompt")}
                rows={6}
                placeholder="Creative direction objectives — interpret, reimagine, translate. No rigid coordinates or grids."
                className={`${inputCls} resize-y min-h-[120px] leading-relaxed`}
              />
            </Field>

            {/* ---------- Thumbnail (CDS-upgrade) ---------- */}
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-center gap-2 mb-2">
                <ImageSquare size={14} weight="bold" className="text-amber-300" />
                <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold">Card Thumbnail</p>
                <span className="text-[10px] text-zinc-600 ml-1">1:1 · shown on the CDS card</span>
              </div>
              <div className="flex items-start gap-3 flex-wrap">
                {/* Live preview — either uploaded-but-unsaved, or currently saved */}
                <div
                  data-testid="dm-thumb-preview"
                  className="relative w-24 h-24 rounded-lg overflow-hidden bg-black/60"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)" }}
                >
                  {thumbPreview ? (
                    <img src={thumbPreview} alt="preview" className="w-full h-full object-cover" />
                  ) : currentThumb ? (
                    <img src={fileUrl(currentThumb)} alt="current" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
                      <ImageSquare size={22} weight="duotone" />
                      <span className="text-[8px] uppercase tracking-[0.18em] mt-1">No image</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    data-testid="dm-thumb-file"
                    type="file"
                    accept="image/*"
                    onChange={(e) => onPickThumb(e.target.files?.[0])}
                    className="hidden"
                  />
                  <button
                    type="button"
                    data-testid="dm-thumb-pick"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/5 hover:bg-white/15 text-xs font-semibold transition-colors"
                  >
                    <UploadSimple size={12} weight="bold" />
                    {currentThumb || thumbPreview ? "Replace image" : "Upload image"}
                  </button>
                  {thumbPreview && (
                    <button
                      type="button"
                      data-testid="dm-thumb-clear-picked"
                      onClick={clearPickedThumb}
                      className="text-[10px] text-zinc-500 hover:text-white transition-colors text-left"
                    >
                      Clear pending upload
                    </button>
                  )}
                  {editing && currentThumb && (
                    <button
                      type="button"
                      data-testid="dm-thumb-remove"
                      onClick={removeExistingThumbnail}
                      className="text-[10px] text-red-300 hover:text-red-200 transition-colors text-left"
                    >
                      Remove current thumbnail
                    </button>
                  )}
                  <p className="text-[10px] text-zinc-600 max-w-[220px] leading-snug">
                    PNG / JPG / WebP · max 8MB. Any aspect ratio is fine — the card displays a 1:1 crop.
                  </p>
                </div>
              </div>
            </div>

            {/* ---------- Tags + section flags ---------- */}
            <Field label="Tags — 3 shown as card pills (comma-separated, e.g. Neon, Nightlife, Premium)">
              <input data-testid="dm-tags-input" value={form.tags} onChange={set("tags")} placeholder="Neon, Nightlife, Premium" className={inputCls} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex flex-col gap-1 rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="flex items-center gap-2">
                  <Sparkle size={13} weight="fill" className="text-amber-300" />
                  <Switch data-testid="dm-quickstart-toggle" checked={!!form.is_quickstart} onCheckedChange={(v) => setForm((f) => ({ ...f, is_quickstart: v }))} className="data-[state=checked]:bg-amber-400" />
                  <span className="text-xs font-semibold text-zinc-300">Quick Start</span>
                </div>
                <input data-testid="dm-quickstart-sort" type="number" placeholder="Sort # (optional)" value={form.quickstart_sort_order} onChange={set("quickstart_sort_order")} className={`${inputCls} mt-1 text-xs`} />
              </div>
              <div className="flex flex-col gap-1 rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-300 text-xs font-bold">NEW</span>
                  <Switch data-testid="dm-freshdrop-toggle" checked={!!form.is_fresh_drop} onCheckedChange={(v) => setForm((f) => ({ ...f, is_fresh_drop: v }))} className="data-[state=checked]:bg-emerald-400" />
                  <span className="text-xs font-semibold text-zinc-300">Fresh Drop</span>
                </div>
                <input data-testid="dm-freshdrop-sort" type="number" placeholder="Sort # (optional)" value={form.fresh_drop_sort_order} onChange={set("fresh_drop_sort_order")} className={`${inputCls} mt-1 text-xs`} />
              </div>
              <div className="flex flex-col gap-1 rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="flex items-center gap-2">
                  <Star size={13} weight="fill" className="text-fuchsia-300" />
                  <Switch data-testid="dm-featured-toggle" checked={!!form.is_featured} onCheckedChange={(v) => setForm((f) => ({ ...f, is_featured: v }))} className="data-[state=checked]:bg-fuchsia-400" />
                  <span className="text-xs font-semibold text-zinc-300">Featured</span>
                </div>
                <input data-testid="dm-featured-until" type="datetime-local" value={form.featured_until} onChange={set("featured_until")} className={`${inputCls} mt-1 text-xs`} placeholder="Featured until" />
              </div>
            </div>
            <div className="flex items-center gap-5 flex-wrap">
              <Field label="Sort Order">
                <input data-testid="dm-order-input" type="number" value={form.sort_order} onChange={set("sort_order")} className={`${inputCls} w-24`} />
              </Field>
              <div className="flex items-center gap-2 mt-5">
                <Switch data-testid="dm-form-active" checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} className="data-[state=checked]:bg-amber-400" />
                <span className="text-xs text-zinc-400">{form.is_active ? "Active — visible to users" : "Inactive — hidden"}</span>
              </div>
              <button
                data-testid="dm-save-btn"
                onClick={save}
                disabled={saving}
                className="ml-auto inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-bold transition-colors disabled:opacity-60"
              >
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : editing ? "Save Changes" : "Create Prompt"}
              </button>
            </div>
          </div>
        )}

        {list.length === 0 ? (
          <p className="text-xs text-zinc-600">No prompts yet — create your first Creative Director.</p>
        ) : (
          <div className="flex flex-col divide-y divide-white/5">
            {list.map((d) => (
              <div key={d.id} data-testid={`dm-row-${d.id}`} className="flex items-center gap-3 py-3">
                {/* Tiny thumbnail badge — placeholder when null */}
                <div
                  data-testid={`dm-row-thumb-${d.id}`}
                  className="shrink-0 w-10 h-10 rounded-md overflow-hidden bg-black/50 flex items-center justify-center"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)" }}
                  title={d.thumbnail ? "Has thumbnail" : "No thumbnail — placeholder shown"}
                >
                  {d.thumbnail
                    ? <img src={fileUrl(d.thumbnail)} alt="" className="w-full h-full object-cover" />
                    : <ImageSquare size={16} weight="duotone" className="text-zinc-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-bold truncate ${d.is_active ? "text-white" : "text-zinc-500"}`}>{d.name}</p>
                    {d.category && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400">{d.category}</span>}
                    <span className="text-[9px] text-zinc-600">#{d.sort_order ?? 0}</span>
                    {!d.is_active && <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-zinc-500"><EyeSlash size={10} /> hidden</span>}
                  </div>
                  {d.short_description && <p className="text-[11px] text-zinc-500 truncate">{d.short_description}</p>}
                </div>
                <Switch
                  data-testid={`dm-active-toggle-${d.id}`}
                  checked={!!d.is_active}
                  onCheckedChange={() => toggleActive(d)}
                  className="data-[state=checked]:bg-amber-400"
                />
                <button data-testid={`dm-edit-${d.id}`} onClick={() => openEdit(d)} title="Edit" className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-zinc-300 hover:text-white transition-colors">
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button data-testid={`dm-delete-${d.id}`} onClick={() => remove(d)} title="Delete" className="h-8 w-8 rounded-lg bg-white/5 hover:bg-red-600 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Global default design direction */}
      <div className="rounded-2xl border border-white/10 bg-[#111] p-5">
        <p className="font-display text-base font-bold mb-1">Default Design Direction Prompt</p>
        <p className="text-[11px] text-zinc-500 mb-3">Silently appended to EVERY generation (combined with the user's typed direction and any selected Director). Leave empty to disable.</p>
        <textarea
          data-testid="ddp-textarea"
          value={defaultDir}
          onChange={(e) => setDefaultDir(e.target.value)}
          rows={5}
          placeholder="Global hidden creative direction for GPT Image 2…"
          className={`${inputCls} resize-y min-h-[100px] leading-relaxed`}
        />
        <div className="flex justify-end mt-3">
          <button
            data-testid="ddp-save-btn"
            onClick={saveDefaultDir}
            disabled={savingDir || !dirLoaded}
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors disabled:opacity-60"
          >
            <FloppyDisk size={14} weight="bold" /> {savingDir ? "Saving…" : "Save Default Direction"}
          </button>
        </div>
      </div>
    </div>
  );
}
